/**
 * GameManagementPage.js — Consolidated game management: scores, venues, referees, editing
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import * as api from '../services/api';
import { formatDate } from '../utils/dateFormat';
import { getSportConfig, getOfficialTitle } from '../utils/sportConfig';
import './GameManagementPage.css';

const statusColors = {
  scheduled: 'var(--color-card)',
  completed: '#f0fdf4',
  postponed: '#fffbeb',
  cancelled: '#fef2f2',
  forfeit: '#fff7ed'
};

const statusColorsDark = {
  scheduled: 'var(--color-section-bg)',
  completed: '#0a2a0a',
  postponed: '#2a2500',
  cancelled: '#2a0a0a',
  forfeit: '#2a1a00'
};

function GameManagementPage({ leagueId, onBack }) {
  const { user } = useAuth();
  const userPlan = user?.plan || 'free';
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';

  // Core data
  const [league, setLeague] = useState(null);
  const [venues, setVenues] = useState([]);
  const [referees, setReferees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  // Filters
  const [filterWeek, setFilterWeek] = useState('all');
  const [filterTeam, setFilterTeam] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVenue, setFilterVenue] = useState('all');

  // Inline score editing
  const [scores, setScores] = useState({});
  const [editingScore, setEditingScore] = useState(null); // gameId
  const [saving, setSaving] = useState({});

  // Bulk mode
  const [bulkMode, setBulkMode] = useState(null); // null|'score'|'venue'|'referee'
  const [bulkVenueId, setBulkVenueId] = useState('');
  const [bulkRefereeId, setBulkRefereeId] = useState('');
  const [savingBulk, setSavingBulk] = useState(false);

  // Draft editing
  const [editingGameId, setEditingGameId] = useState(null);
  const [editGameForm, setEditGameForm] = useState({ date: '', time: '', team1Id: '', team2Id: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  // Swap mode
  const [swapMode, setSwapMode] = useState(false);
  const [swapSelection, setSwapSelection] = useState(null);
  const [swappingGames, setSwappingGames] = useState(false);

  // Fairness warning
  const [fairnessWarning, setFairnessWarning] = useState(null);

  // Referee links
  const [refLinks, setRefLinks] = useState(null);
  const [generatingRefLinks, setGeneratingRefLinks] = useState(false);

  const showToast = useCallback((message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const promises = [api.getLeague(leagueId)];
      if (userPlan !== 'free') promises.push(api.getVenues());
      else promises.push(Promise.resolve({ venues: [] }));
      if (userPlan === 'pro') promises.push(api.getReferees());
      else promises.push(Promise.resolve({ referees: [] }));

      const [leagueResult, venueResult, refereeResult] = await Promise.all(promises);
      const leagueData = leagueResult.league;
      setLeague(leagueData);

      setVenues(venueResult.venues || []);
      const sport = leagueData.sport || 'general';
      const allRefs = refereeResult.referees || [];
      setReferees(allRefs.filter(r => !r.sports || r.sports.length === 0 || r.sports.includes(sport)));

      // Initialize scores from existing game data
      const initialScores = {};
      (leagueData.games || []).forEach(game => {
        if (game.played) {
          initialScores[game.id] = { team1Score: game.team1Score, team2Score: game.team2Score };
        }
      });
      setScores(initialScores);
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [leagueId, userPlan]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // --- Derived data ---

  if (loading) {
    return <div className="game-mgmt-page"><div className="game-mgmt-loading">Loading games...</div></div>;
  }

  if (error || !league) {
    return (
      <div className="game-mgmt-page">
        <div className="game-mgmt-error">
          <h2>Error</h2>
          <p>{error || 'League not found'}</p>
          <button className="game-mgmt-back-btn" onClick={onBack}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  const teamMap = {};
  const teams = (league.teams || []).sort((a, b) => a.name.localeCompare(b.name));
  teams.forEach(t => { teamMap[t.id] = t; });

  const sportConfig = getSportConfig(league.sport);
  const officialTitle = getOfficialTitle(league.sport);
  const isDraft = league.status === 'draft';

  const regularGames = (league.games || [])
    .filter(g => !g.isPlayoff)
    .map(g => ({
      ...g,
      team1Name: teamMap[g.team1Id]?.name || 'TBD',
      team2Name: teamMap[g.team2Id]?.name || 'TBD'
    }));

  // Filter
  let filteredGames = regularGames;
  if (filterWeek !== 'all') filteredGames = filteredGames.filter(g => g.week === parseInt(filterWeek));
  if (filterTeam !== 'all') filteredGames = filteredGames.filter(g => g.team1Id === filterTeam || g.team2Id === filterTeam);
  if (filterStatus !== 'all') filteredGames = filteredGames.filter(g => (g.status || 'scheduled') === filterStatus);
  if (filterVenue !== 'all') {
    if (filterVenue === 'none') filteredGames = filteredGames.filter(g => !g.venueId);
    else filteredGames = filteredGames.filter(g => g.venueId === filterVenue);
  }

  const weeks = [...new Set(regularGames.map(g => g.week))].sort((a, b) => a - b);
  const hasActiveFilter = filterWeek !== 'all' || filterTeam !== 'all' || filterStatus !== 'all' || filterVenue !== 'all';

  // Group filtered games by week
  const gamesByWeek = {};
  filteredGames.forEach(g => {
    if (!gamesByWeek[g.week]) gamesByWeek[g.week] = { week: g.week, date: g.date, games: [] };
    gamesByWeek[g.week].games.push(g);
  });
  const sortedWeeks = Object.values(gamesByWeek).sort((a, b) => a.week - b.week);

  // Stats
  const totalGames = regularGames.length;
  const playedGames = regularGames.filter(g => g.played).length;
  const unscoredGames = regularGames.filter(g => !g.played && (g.status || 'scheduled') === 'scheduled').length;

  // --- Handlers ---

  const handleScoreChange = (gameId, field, value) => {
    const numValue = value === '' ? '' : parseInt(value, 10);
    if (value !== '' && (isNaN(numValue) || numValue < 0)) return;
    setScores(prev => ({ ...prev, [gameId]: { ...prev[gameId], [field]: numValue } }));
  };

  // Win/Loss only: derive dropdown value from scores
  const getWinnerValue = (gameScores) => {
    if (!gameScores || gameScores.team1Score == null || gameScores.team2Score == null ||
        gameScores.team1Score === '' || gameScores.team2Score === '') return '';
    if (gameScores.team1Score === 1 && gameScores.team2Score === 0) return 'team1';
    if (gameScores.team1Score === 0 && gameScores.team2Score === 1) return 'team2';
    if (gameScores.team1Score === 0 && gameScores.team2Score === 0) return 'draw';
    return '';
  };

  // Win/Loss only: handle winner dropdown change
  const handleWinnerChange = (gameId, value) => {
    if (value === 'team1') {
      setScores(prev => ({ ...prev, [gameId]: { team1Score: 1, team2Score: 0 } }));
    } else if (value === 'team2') {
      setScores(prev => ({ ...prev, [gameId]: { team1Score: 0, team2Score: 1 } }));
    } else if (value === 'draw') {
      setScores(prev => ({ ...prev, [gameId]: { team1Score: 0, team2Score: 0 } }));
    } else {
      setScores(prev => ({ ...prev, [gameId]: { team1Score: '', team2Score: '' } }));
    }
  };

  const handleSaveScore = async (game) => {
    const gameScores = scores[game.id];
    if (!gameScores || gameScores.team1Score === '' || gameScores.team2Score === '' ||
        gameScores.team1Score == null || gameScores.team2Score == null) {
      showToast('Please enter both scores');
      return;
    }
    setSaving(prev => ({ ...prev, [game.id]: true }));
    try {
      await api.updateGameScore(leagueId, game.id, gameScores.team1Score, gameScores.team2Score);
      setLeague(prev => ({
        ...prev,
        games: prev.games.map(g =>
          g.id === game.id
            ? { ...g, team1Score: gameScores.team1Score, team2Score: gameScores.team2Score, played: true, status: 'completed' }
            : g
        )
      }));
      setEditingScore(null);
      showToast('Score saved!', 'success');
    } catch (err) {
      showToast('Failed to save score: ' + err.message);
    } finally {
      setSaving(prev => ({ ...prev, [game.id]: false }));
    }
  };

  const handleStatusChange = async (game, newStatus, extra = {}) => {
    if (newStatus === 'forfeit' && extra.forfeitTeamId === undefined && !game.forfeitTeamId) {
      setLeague(prev => ({
        ...prev,
        games: prev.games.map(g => g.id === game.id ? { ...g, status: 'forfeit' } : g)
      }));
      return;
    }
    if (newStatus === 'forfeit' && !extra.forfeitTeamId) return;

    setSaving(prev => ({ ...prev, [game.id]: true }));
    try {
      const data = { status: newStatus, ...extra };
      const result = await api.updateGameStatus(leagueId, game.id, data);
      const updatedGame = result.game;
      setLeague(prev => ({
        ...prev,
        games: prev.games.map(g =>
          g.id === game.id
            ? { ...g, status: updatedGame.status, played: updatedGame.played, team1Score: updatedGame.team1Score, team2Score: updatedGame.team2Score, forfeitTeamId: updatedGame.forfeitTeamId, forfeitTeam: updatedGame.forfeitTeam, rescheduledDate: updatedGame.rescheduledDate, rescheduledTime: updatedGame.rescheduledTime }
            : g
        )
      }));
      if (updatedGame.team1Score == null) {
        setScores(prev => { const next = { ...prev }; delete next[game.id]; return next; });
      }
      const labels = { scheduled: 'Reset to scheduled', completed: 'Marked as completed', postponed: 'Game postponed', cancelled: 'Game cancelled', forfeit: 'Forfeit recorded' };
      showToast(labels[newStatus] || 'Status updated', 'success');
    } catch (err) {
      showToast('Failed: ' + err.message);
      loadData();
    } finally {
      setSaving(prev => ({ ...prev, [game.id]: false }));
    }
  };

  const handleAssignVenue = async (gameId, venueId) => {
    setSaving(prev => ({ ...prev, [gameId]: true }));
    try {
      await api.assignVenueToGame(leagueId, gameId, venueId || null);
      const venue = venueId ? venues.find(v => v.id === venueId) : null;
      setLeague(prev => ({
        ...prev,
        games: prev.games.map(g =>
          g.id === gameId ? { ...g, venueId: venueId || null, venue: venue ? { id: venue.id, name: venue.name } : null } : g
        )
      }));
    } catch (err) {
      if (err.response?.status === 409) {
        showToast(err.response.data?.error || 'Venue conflict — venue is already booked at this time');
      } else {
        showToast('Failed to assign venue: ' + err.message);
      }
    } finally {
      setSaving(prev => ({ ...prev, [gameId]: false }));
    }
  };

  const handleAssignReferee = async (gameId, refereeId) => {
    setSaving(prev => ({ ...prev, [gameId]: true }));
    try {
      await api.assignRefereeToGame(leagueId, gameId, refereeId || null);
      const ref = refereeId ? referees.find(r => r.id === refereeId) : null;
      setLeague(prev => ({
        ...prev,
        games: prev.games.map(g =>
          g.id === gameId ? { ...g, refereeId: refereeId || null, referee: ref ? { id: ref.id, name: ref.name } : null } : g
        )
      }));
    } catch (err) {
      showToast('Failed to assign ' + officialTitle.toLowerCase() + ': ' + err.message);
    } finally {
      setSaving(prev => ({ ...prev, [gameId]: false }));
    }
  };

  // --- Bulk operations ---

  const handleBulkSaveScores = async () => {
    const scoresToSubmit = [];
    filteredGames.forEach(g => {
      const s = scores[g.id];
      if (!s) return;
      const gs = g.status || 'scheduled';
      if (gs !== 'scheduled' && gs !== 'completed') return;
      if (s.team1Score === '' || s.team1Score == null || s.team2Score === '' || s.team2Score == null) return;
      if (isNaN(s.team1Score) || isNaN(s.team2Score) || s.team1Score < 0 || s.team2Score < 0) return;
      if (!sportConfig.allowTies && s.team1Score === s.team2Score) return;
      // Skip already-saved-with-same-score games
      if (g.played && g.team1Score === s.team1Score && g.team2Score === s.team2Score) return;
      scoresToSubmit.push({ gameId: g.id, team1Score: s.team1Score, team2Score: s.team2Score });
    });

    if (scoresToSubmit.length === 0) {
      showToast('No new scores to save');
      return;
    }

    setSavingBulk(true);
    try {
      const result = await api.bulkScoreEntry(leagueId, scoresToSubmit);
      // Refresh data to get updated standings
      await loadData();
      const errCount = result.errors?.length || 0;
      if (errCount > 0) {
        showToast(`${result.updated} scores saved, ${errCount} failed`, 'error');
      } else {
        showToast(`${result.updated} scores saved!`, 'success');
      }
      setBulkMode(null);
    } catch (err) {
      showToast('Failed to save scores: ' + err.message);
    } finally {
      setSavingBulk(false);
    }
  };

  const handleBulkAssignVenue = async () => {
    if (!bulkVenueId) return;
    setSavingBulk(true);
    let successCount = 0;
    let failCount = 0;
    for (const g of filteredGames) {
      if (g.venueId === bulkVenueId) continue;
      try {
        await api.assignVenueToGame(leagueId, g.id, bulkVenueId);
        successCount++;
      } catch {
        failCount++;
      }
    }
    await loadData();
    setSavingBulk(false);
    if (failCount > 0) {
      showToast(`${successCount} assigned, ${failCount} conflicts`, failCount > successCount ? 'error' : 'success');
    } else {
      showToast(`Venue assigned to ${successCount} games`, 'success');
    }
    setBulkVenueId('');
  };

  const handleBulkAssignReferee = async () => {
    if (!bulkRefereeId) return;
    setSavingBulk(true);
    let successCount = 0;
    let failCount = 0;
    for (const g of filteredGames) {
      if (g.refereeId === bulkRefereeId) continue;
      try {
        await api.assignRefereeToGame(leagueId, g.id, bulkRefereeId);
        successCount++;
      } catch {
        failCount++;
      }
    }
    await loadData();
    setSavingBulk(false);
    showToast(`${officialTitle} assigned to ${successCount} games${failCount ? `, ${failCount} failed` : ''}`, failCount ? 'error' : 'success');
    setBulkRefereeId('');
  };

  // --- Draft editing ---

  const handleStartEdit = (game) => {
    setEditingGameId(game.id);
    setEditGameForm({ date: game.date || '', time: game.time || '', team1Id: game.team1Id || '', team2Id: game.team2Id || '' });
  };

  const handleSaveEdit = async () => {
    if (!editingGameId) return;
    setSavingEdit(true);
    try {
      const result = await api.updateGame(leagueId, editingGameId, editGameForm);
      setLeague(prev => ({
        ...prev,
        games: prev.games.map(g =>
          g.id === editingGameId
            ? { ...g, date: result.game.date, time: result.game.time, week: result.game.week, team1Id: result.game.team1Id, team2Id: result.game.team2Id }
            : g
        )
      }));
      if (result.fairness?.gradeChanged) {
        setFairnessWarning(`Fairness grade changed from ${result.fairness.oldGrade} to ${result.fairness.newGrade}`);
      }
      setEditingGameId(null);
      showToast('Game updated!', 'success');
    } catch (err) {
      showToast('Failed: ' + err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  // --- Swap ---

  const handleSwapClick = async (game) => {
    if (!swapMode || game.played) return;
    if (!swapSelection) {
      setSwapSelection(game.id);
      return;
    }
    if (swapSelection === game.id) {
      setSwapSelection(null);
      return;
    }
    setSwappingGames(true);
    try {
      const result = await api.swapGames(leagueId, swapSelection, game.id);
      // Apply swapped data
      const swappedMap = {};
      result.games.forEach(g => { swappedMap[g.id] = g; });
      setLeague(prev => ({
        ...prev,
        games: prev.games.map(g => swappedMap[g.id] ? { ...g, week: swappedMap[g.id].week, date: swappedMap[g.id].date, time: swappedMap[g.id].time } : g)
      }));
      if (result.fairness?.gradeChanged) {
        setFairnessWarning(`Fairness grade changed from ${result.fairness.oldGrade} to ${result.fairness.newGrade}`);
      }
      setSwapSelection(null);
      showToast('Games swapped!', 'success');
    } catch (err) {
      showToast('Failed: ' + err.message);
    } finally {
      setSwappingGames(false);
    }
  };

  // --- Generate referee links ---

  const handleGenerateRefLinks = async () => {
    const refsWithEmail = referees.filter(r => r.email);
    if (refsWithEmail.length === 0) {
      showToast(`No ${officialTitle.toLowerCase()}s have email addresses`);
      return;
    }
    setGeneratingRefLinks(true);
    try {
      const links = [];
      for (const ref of refsWithEmail) {
        const result = await api.generateRefereeToken(ref.id);
        links.push({ name: ref.name, email: ref.email, url: `${window.location.origin}/referee/${result.token}` });
      }
      setRefLinks(links);
      showToast(`${links.length} link(s) generated!`, 'success');
    } catch (err) {
      showToast('Failed to generate links: ' + err.message);
    } finally {
      setGeneratingRefLinks(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => showToast('Copied!', 'success')).catch(() => showToast('Failed to copy'));
  };

  const copyAllRefLinks = () => {
    if (!refLinks) return;
    const text = refLinks.map(l => `${l.name} (${l.email}): ${l.url}`).join('\n');
    copyToClipboard(text);
  };

  // Bulk score count
  const bulkScoreCount = bulkMode === 'score' ? filteredGames.filter(g => {
    const s = scores[g.id];
    const gs = g.status || 'scheduled';
    if (gs !== 'scheduled' && gs !== 'completed') return false;
    if (!s || s.team1Score === '' || s.team1Score == null || s.team2Score === '' || s.team2Score == null) return false;
    if (!sportConfig.allowTies && s.team1Score === s.team2Score) return false;
    if (g.played && g.team1Score === s.team1Score && g.team2Score === s.team2Score) return false;
    return true;
  }).length : 0;

  const clearFilters = () => {
    setFilterWeek('all');
    setFilterTeam('all');
    setFilterStatus('all');
    setFilterVenue('all');
  };

  const toggleBulkMode = (mode) => {
    if (bulkMode === mode) {
      setBulkMode(null);
    } else {
      setBulkMode(mode);
      setSwapMode(false);
      setSwapSelection(null);
      setEditingGameId(null);
    }
  };

  const toggleSwapMode = () => {
    setSwapMode(!swapMode);
    setSwapSelection(null);
    setBulkMode(null);
    setEditingGameId(null);
  };

  const colorsMap = isDark ? statusColorsDark : statusColors;

  // --- Render ---

  return (
    <div className="game-mgmt-page">
      {/* Header */}
      <div className="game-mgmt-header">
        <div className="game-mgmt-top-row">
          <button className="game-mgmt-back-btn" onClick={onBack}>← Dashboard</button>
          <h1 className="game-mgmt-title">{league.name}</h1>
          <div className="game-mgmt-meta">
            <span className={`game-mgmt-badge game-mgmt-badge-${isDraft ? 'draft' : 'published'}`}>
              {isDraft ? 'Draft' : 'Published'}
            </span>
            {league.sport && league.sport !== 'general' && (
              <span className="game-mgmt-badge game-mgmt-badge-sport">{sportConfig.label}</span>
            )}
          </div>
        </div>
        <div className="game-mgmt-stats-row">
          <div className="game-mgmt-stat-card">
            <div className="game-mgmt-stat-value">{totalGames}</div>
            <div className="game-mgmt-stat-label">Games</div>
          </div>
          <div className="game-mgmt-stat-card">
            <div className="game-mgmt-stat-value">{playedGames}</div>
            <div className="game-mgmt-stat-label">Played</div>
          </div>
          <div className="game-mgmt-stat-card">
            <div className="game-mgmt-stat-value">{unscoredGames}</div>
            <div className="game-mgmt-stat-label">Unscored</div>
          </div>
          <div className="game-mgmt-stat-card">
            <div className="game-mgmt-stat-value">{weeks.length}</div>
            <div className="game-mgmt-stat-label">Weeks</div>
          </div>
        </div>
      </div>

      {/* Fairness warning */}
      {fairnessWarning && (
        <div className="game-mgmt-fairness-warning">
          <span>{fairnessWarning}</span>
          <button className="game-mgmt-fairness-dismiss" onClick={() => setFairnessWarning(null)}>×</button>
        </div>
      )}

      {/* Filter bar */}
      <div className="game-mgmt-filter-bar">
        <div className="game-mgmt-filter-group">
          <label>Week</label>
          <select value={filterWeek} onChange={(e) => setFilterWeek(e.target.value)}>
            <option value="all">All Weeks</option>
            {weeks.map(w => <option key={w} value={w}>Week {w}</option>)}
          </select>
        </div>
        <div className="game-mgmt-filter-group">
          <label>Team</label>
          <select value={filterTeam} onChange={(e) => setFilterTeam(e.target.value)}>
            <option value="all">All Teams</option>
            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div className="game-mgmt-filter-group">
          <label>Status</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="scheduled">Scheduled</option>
            <option value="completed">Completed</option>
            <option value="postponed">Postponed</option>
            <option value="cancelled">Cancelled</option>
            <option value="forfeit">Forfeit</option>
          </select>
        </div>
        {userPlan !== 'free' && venues.length > 0 && (
          <div className="game-mgmt-filter-group">
            <label>Venue</label>
            <select value={filterVenue} onChange={(e) => setFilterVenue(e.target.value)}>
              <option value="all">All Venues</option>
              <option value="none">No Venue</option>
              {venues.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
        )}
        {hasActiveFilter && (
          <button className="game-mgmt-clear-filters" onClick={clearFilters}>Clear Filters</button>
        )}
      </div>

      <div className="game-mgmt-results-count">
        Showing {filteredGames.length} of {totalGames} games
      </div>

      {/* Action bar */}
      <div className="game-mgmt-action-bar">
        {userPlan === 'pro' && !isDraft && (
          <button
            className={`game-mgmt-action-btn${bulkMode === 'score' ? ' game-mgmt-active' : ''}`}
            onClick={() => toggleBulkMode('score')}
          >
            Bulk Score
          </button>
        )}
        {userPlan !== 'free' && !isDraft && (
          <button
            className={`game-mgmt-action-btn${bulkMode === 'venue' ? ' game-mgmt-active' : ''}`}
            onClick={() => toggleBulkMode('venue')}
          >
            Bulk Venues
          </button>
        )}
        {userPlan === 'pro' && !isDraft && (
          <button
            className={`game-mgmt-action-btn${bulkMode === 'referee' ? ' game-mgmt-active' : ''}`}
            onClick={() => toggleBulkMode('referee')}
          >
            Bulk {officialTitle}s
          </button>
        )}
        {isDraft && (
          <>
            <button
              className={`game-mgmt-action-btn${swapMode ? ' game-mgmt-active' : ''}`}
              onClick={toggleSwapMode}
              disabled={swappingGames}
            >
              {swapMode ? 'Exit Swap Mode' : 'Swap Games'}
            </button>
          </>
        )}
        {userPlan === 'pro' && referees.some(r => r.email) && (
          <>
            <div className="game-mgmt-action-separator" />
            <button
              className="game-mgmt-action-btn"
              onClick={handleGenerateRefLinks}
              disabled={generatingRefLinks}
            >
              {generatingRefLinks ? 'Generating...' : `Generate ${officialTitle} Links`}
            </button>
          </>
        )}
      </div>

      {/* Bulk "Set All" bars */}
      {bulkMode === 'venue' && (
        <div className="game-mgmt-bulk-bar">
          <label>Set all visible games to:</label>
          <select value={bulkVenueId} onChange={(e) => setBulkVenueId(e.target.value)}>
            <option value="">Select venue...</option>
            {venues.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <button onClick={handleBulkAssignVenue} disabled={!bulkVenueId || savingBulk}>
            {savingBulk ? 'Assigning...' : 'Apply'}
          </button>
        </div>
      )}
      {bulkMode === 'referee' && (
        <div className="game-mgmt-bulk-bar">
          <label>Set all visible games to:</label>
          <select value={bulkRefereeId} onChange={(e) => setBulkRefereeId(e.target.value)}>
            <option value="">Select {officialTitle.toLowerCase()}...</option>
            {referees.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <button onClick={handleBulkAssignReferee} disabled={!bulkRefereeId || savingBulk}>
            {savingBulk ? 'Assigning...' : 'Apply'}
          </button>
        </div>
      )}

      {/* Referee links panel */}
      {refLinks && (
        <div className="game-mgmt-ref-links-panel">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <strong style={{ color: 'var(--color-text)' }}>{officialTitle} Links</strong>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="game-mgmt-copy-btn" onClick={copyAllRefLinks}>Copy All</button>
              <button className="game-mgmt-copy-btn" onClick={() => setRefLinks(null)}>Close</button>
            </div>
          </div>
          {refLinks.map((link, i) => (
            <div key={i} className="game-mgmt-ref-link-row">
              <span className="game-mgmt-ref-link-info">{link.name}</span>
              <span className="game-mgmt-ref-link-url">{link.url}</span>
              <button className="game-mgmt-copy-btn" onClick={() => copyToClipboard(link.url)}>Copy</button>
            </div>
          ))}
        </div>
      )}

      {/* Swap mode hint */}
      {swapMode && (
        <div className="game-mgmt-fairness-warning">
          <span>{swapSelection ? 'Click another game to swap with the selected game' : 'Click a game to select it for swapping'}</span>
        </div>
      )}

      {/* Game list */}
      {sortedWeeks.length === 0 ? (
        <div className="game-mgmt-empty">
          {hasActiveFilter ? 'No games match the current filters' : 'No games found for this league'}
        </div>
      ) : (
        sortedWeeks.map(weekData => (
          <div key={weekData.week} className="game-mgmt-week-card">
            <div className="game-mgmt-week-header">
              <span className="game-mgmt-week-label">Week {weekData.week}</span>
              <span className="game-mgmt-week-date">{formatDate(weekData.date)}</span>
            </div>
            {weekData.games.map(game => {
              const gameStatus = game.status || 'scheduled';
              const isScoreable = gameStatus === 'scheduled' || gameStatus === 'completed';
              const gameScores = scores[game.id] || {};
              const hasScoreValues = gameScores.team1Score != null && gameScores.team1Score !== '' && gameScores.team2Score != null && gameScores.team2Score !== '';
              const isTied = hasScoreValues && gameScores.team1Score === gameScores.team2Score;
              const tieBlocked = isTied && !sportConfig.allowTies;
              const isSaved = game.played && gameStatus === 'completed' && game.team1Score === gameScores.team1Score && game.team2Score === gameScores.team2Score;
              const showScoreInputs = (bulkMode === 'score' && isScoreable) || editingScore === game.id;
              const isSwapSelected = swapMode && swapSelection === game.id;

              return (
                <React.Fragment key={game.id}>
                  <div
                    className={`game-mgmt-game-row${
                      swapMode && game.played ? ' game-mgmt-game-row-dimmed' : ''
                    }${isSwapSelected ? ' game-mgmt-game-row-swap-selected' : ''
                    }${swapMode && !game.played ? ' game-mgmt-game-row-swap-target' : ''}`}
                    style={{ background: colorsMap[gameStatus] || 'transparent' }}
                    onClick={swapMode && !game.played ? () => handleSwapClick(game) : undefined}
                  >
                    {/* Time */}
                    <span className="game-mgmt-game-time">{game.time || '--'}</span>

                    {/* Matchup / Score */}
                    <div className="game-mgmt-game-matchup">
                      {isScoreable && showScoreInputs ? (
                        <div className="game-mgmt-score-inputs">
                          {sportConfig.winLossOnly ? (
                            <>
                              <span className="game-mgmt-team-name">{game.team1Name}</span>
                              <select
                                className="game-mgmt-winner-select"
                                value={getWinnerValue(gameScores)}
                                onChange={(e) => { e.stopPropagation(); handleWinnerChange(game.id, e.target.value); }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <option value="">Select result...</option>
                                <option value="team1">{game.team1Name} wins</option>
                                <option value="team2">{game.team2Name} wins</option>
                                <option value="draw">Draw</option>
                              </select>
                              <span className="game-mgmt-team-name">{game.team2Name}</span>
                              {bulkMode !== 'score' && (
                                <button
                                  className="game-mgmt-score-save-btn"
                                  onClick={(e) => { e.stopPropagation(); handleSaveScore(game); }}
                                  disabled={!hasScoreValues || saving[game.id]}
                                >
                                  {saving[game.id] ? '...' : isSaved ? '✓' : 'Save'}
                                </button>
                              )}
                            </>
                          ) : (
                            <>
                              <span className="game-mgmt-team-name">{game.team1Name}</span>
                              <input
                                type="number" min="0"
                                className={`game-mgmt-score-input${tieBlocked ? ' game-mgmt-tie-error' : ''}`}
                                value={gameScores.team1Score ?? ''}
                                onChange={(e) => handleScoreChange(game.id, 'team1Score', e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                placeholder="-"
                              />
                              <span className="game-mgmt-score-dash">-</span>
                              <input
                                type="number" min="0"
                                className={`game-mgmt-score-input${tieBlocked ? ' game-mgmt-tie-error' : ''}`}
                                value={gameScores.team2Score ?? ''}
                                onChange={(e) => handleScoreChange(game.id, 'team2Score', e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                placeholder="-"
                              />
                              <span className="game-mgmt-team-name">{game.team2Name}</span>
                              {bulkMode !== 'score' && (
                                <button
                                  className="game-mgmt-score-save-btn"
                                  onClick={(e) => { e.stopPropagation(); handleSaveScore(game); }}
                                  disabled={!hasScoreValues || saving[game.id] || tieBlocked}
                                >
                                  {saving[game.id] ? '...' : isSaved ? '✓' : 'Save'}
                                </button>
                              )}
                            </>
                          )}
                          {sportConfig.supportsBestOf && league.bestOf && (
                            <span style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginLeft: '0.5rem', whiteSpace: 'nowrap' }}>
                              Best of {league.bestOf}
                            </span>
                          )}
                        </div>
                      ) : gameStatus === 'forfeit' ? (
                        <span style={{ color: '#e67e22', fontWeight: 600 }}>
                          <span className="game-mgmt-team-name">{game.team1Name}</span>
                          <span className="game-mgmt-vs">vs</span>
                          <span className="game-mgmt-team-name">{game.team2Name}</span>
                          <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem' }}>
                            FORFEIT — {game.forfeitTeam?.name || teamMap[game.forfeitTeamId]?.name || '?'}
                          </span>
                        </span>
                      ) : gameStatus === 'cancelled' ? (
                        <span>
                          <span className="game-mgmt-team-name">{game.team1Name}</span>
                          <span className="game-mgmt-vs">vs</span>
                          <span className="game-mgmt-team-name">{game.team2Name}</span>
                          <span style={{ marginLeft: '0.5rem', color: 'var(--color-danger)', fontWeight: 600, fontSize: '0.8rem' }}>CANCELLED</span>
                        </span>
                      ) : gameStatus === 'postponed' ? (
                        <span>
                          <span className="game-mgmt-team-name">{game.team1Name}</span>
                          <span className="game-mgmt-vs">vs</span>
                          <span className="game-mgmt-team-name">{game.team2Name}</span>
                          <span style={{ marginLeft: '0.5rem', color: '#856404', fontWeight: 600, fontSize: '0.8rem' }}>
                            POSTPONED{game.rescheduledDate ? ` → ${formatDate(game.rescheduledDate)}` : ''}
                          </span>
                        </span>
                      ) : game.played ? (
                        <span>
                          <span className="game-mgmt-team-name">{game.team1Name}</span>
                          {sportConfig.winLossOnly ? (
                            <span className="game-mgmt-score-display"
                              onClick={userPlan === 'pro' && !swapMode ? (e) => { e.stopPropagation(); setEditingScore(game.id); } : undefined}
                              title={userPlan === 'pro' ? 'Click to edit result' : ''}
                              style={{ fontWeight: 600 }}
                            >
                              {game.team1Score === 1 && game.team2Score === 0 ? (
                                <><span style={{ color: 'var(--color-success)' }}>Win</span> - <span style={{ color: 'var(--color-danger)' }}>Loss</span></>
                              ) : game.team1Score === 0 && game.team2Score === 1 ? (
                                <><span style={{ color: 'var(--color-danger)' }}>Loss</span> - <span style={{ color: 'var(--color-success)' }}>Win</span></>
                              ) : (
                                <span style={{ color: 'var(--color-text-secondary)' }}>Draw</span>
                              )}
                            </span>
                          ) : (
                            <span className="game-mgmt-score-display"
                              onClick={userPlan === 'pro' && !swapMode ? (e) => { e.stopPropagation(); setEditingScore(game.id); } : undefined}
                              title={userPlan === 'pro' ? 'Click to edit score' : ''}
                            >
                              {game.team1Score} - {game.team2Score}
                            </span>
                          )}
                          <span className="game-mgmt-team-name">{game.team2Name}</span>
                        </span>
                      ) : (
                        <span>
                          <span className="game-mgmt-team-name">{game.team1Name}</span>
                          <span className="game-mgmt-vs"
                            onClick={userPlan === 'pro' && !swapMode ? (e) => { e.stopPropagation(); setEditingScore(game.id); } : undefined}
                            style={userPlan === 'pro' && !swapMode ? { cursor: 'pointer' } : {}}
                            title={userPlan === 'pro' ? 'Click to enter score' : ''}
                          >
                            vs
                          </span>
                          <span className="game-mgmt-team-name">{game.team2Name}</span>
                        </span>
                      )}
                      {tieBlocked && showScoreInputs && (
                        <span className="game-mgmt-no-tie-label"> No ties in {sportConfig.label}</span>
                      )}
                    </div>

                    {/* Status */}
                    {userPlan === 'pro' && !isDraft && !swapMode ? (
                      <select
                        className="game-mgmt-status-select"
                        value={gameStatus}
                        onChange={(e) => handleStatusChange(game, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={saving[game.id]}
                        style={{ background: colorsMap[gameStatus] }}
                      >
                        <option value="scheduled">Scheduled</option>
                        <option value="completed">Completed</option>
                        <option value="postponed">Postponed</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="forfeit">Forfeit</option>
                      </select>
                    ) : !isDraft ? (
                      <span className="game-mgmt-status-badge" style={{
                        background: gameStatus === 'completed' ? 'var(--color-badge-published-bg)' : gameStatus === 'scheduled' ? 'var(--color-section-bg)' : colorsMap[gameStatus],
                        color: gameStatus === 'completed' ? 'var(--color-badge-published-text)' : gameStatus === 'scheduled' ? 'var(--color-text-secondary)' : 'var(--color-text)',
                        border: gameStatus === 'scheduled' ? '1px solid var(--color-border)' : 'none'
                      }}>
                        {gameStatus}
                      </span>
                    ) : null}

                    {/* Venue */}
                    {userPlan !== 'free' && (
                      <div className="game-mgmt-col-venue">
                        <select
                          className="game-mgmt-assign-select"
                          value={game.venueId || ''}
                          onChange={(e) => { e.stopPropagation(); handleAssignVenue(game.id, e.target.value); }}
                          onClick={(e) => e.stopPropagation()}
                          disabled={saving[game.id]}
                          title={game.venue?.name || 'No venue'}
                        >
                          <option value="">— No Venue —</option>
                          {venues.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                        </select>
                      </div>
                    )}

                    {/* Referee */}
                    {userPlan === 'pro' && (
                      <div className="game-mgmt-col-referee">
                        <select
                          className="game-mgmt-assign-select"
                          value={game.refereeId || ''}
                          onChange={(e) => { e.stopPropagation(); handleAssignReferee(game.id, e.target.value); }}
                          onClick={(e) => e.stopPropagation()}
                          disabled={saving[game.id]}
                          title={game.referee?.name || `No ${officialTitle.toLowerCase()}`}
                        >
                          <option value="">— No {officialTitle} —</option>
                          {referees.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                      </div>
                    )}

                    {/* Draft edit button */}
                    {isDraft && !swapMode && !game.played && (
                      <button
                        className="game-mgmt-row-edit-btn"
                        onClick={(e) => { e.stopPropagation(); handleStartEdit(game); }}
                      >
                        Edit
                      </button>
                    )}
                  </div>

                  {/* Forfeit team selector (when status is forfeit and Pro) */}
                  {gameStatus === 'forfeit' && userPlan === 'pro' && !game.forfeitTeamId && (
                    <div className="game-mgmt-edit-form">
                      <label>Forfeited by:</label>
                      <select
                        className="game-mgmt-forfeit-select"
                        value=""
                        onChange={(e) => handleStatusChange(game, 'forfeit', { forfeitTeamId: e.target.value })}
                      >
                        <option value="">Select team...</option>
                        <option value={game.team1Id}>{game.team1Name}</option>
                        <option value={game.team2Id}>{game.team2Name}</option>
                      </select>
                    </div>
                  )}

                  {/* Postpone reschedule fields (when status is postponed and Pro) */}
                  {gameStatus === 'postponed' && userPlan === 'pro' && (
                    <div className="game-mgmt-edit-form">
                      <label>Reschedule:</label>
                      <div className="game-mgmt-postpone-fields">
                        <input
                          type="date"
                          value={game.rescheduledDate || ''}
                          onChange={(e) => handleStatusChange(game, 'postponed', { rescheduledDate: e.target.value, rescheduledTime: game.rescheduledTime })}
                        />
                        <input
                          type="text"
                          placeholder="Time"
                          value={game.rescheduledTime || ''}
                          onChange={(e) => handleStatusChange(game, 'postponed', { rescheduledDate: game.rescheduledDate, rescheduledTime: e.target.value })}
                          style={{ width: 80 }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Draft inline edit form */}
                  {editingGameId === game.id && (
                    <div className="game-mgmt-edit-form">
                      <label>Date</label>
                      <input type="date" value={editGameForm.date} onChange={(e) => setEditGameForm(f => ({ ...f, date: e.target.value }))} />
                      <label>Time</label>
                      <input type="text" value={editGameForm.time} onChange={(e) => setEditGameForm(f => ({ ...f, time: e.target.value }))} style={{ width: 90 }} />
                      <label>Team 1</label>
                      <select value={editGameForm.team1Id} onChange={(e) => setEditGameForm(f => ({ ...f, team1Id: e.target.value }))}>
                        {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                      <label>Team 2</label>
                      <select value={editGameForm.team2Id} onChange={(e) => setEditGameForm(f => ({ ...f, team2Id: e.target.value }))}>
                        {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                      <div className="game-mgmt-edit-form-btns">
                        <button className="game-mgmt-save-btn" onClick={handleSaveEdit} disabled={savingEdit}>
                          {savingEdit ? '...' : 'Save'}
                        </button>
                        <button className="game-mgmt-cancel-btn" onClick={() => setEditingGameId(null)}>Cancel</button>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        ))
      )}

      {/* Bulk score footer */}
      {bulkMode === 'score' && (
        <div className="game-mgmt-bulk-footer">
          <span style={{ color: 'var(--color-text)', fontSize: '0.9rem', fontWeight: 500 }}>
            {bulkScoreCount} score{bulkScoreCount !== 1 ? 's' : ''} to save
          </span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="game-mgmt-bulk-cancel-btn" onClick={() => setBulkMode(null)}>Cancel</button>
            <button
              className="game-mgmt-bulk-save-btn"
              onClick={handleBulkSaveScores}
              disabled={bulkScoreCount === 0 || savingBulk}
            >
              {savingBulk ? 'Saving...' : `Save All Scores (${bulkScoreCount})`}
            </button>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          className="game-mgmt-toast"
          style={{ background: toast.type === 'success' ? '#2e7d32' : '#c62828' }}
          onClick={() => setToast(null)}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

export default GameManagementPage;
